"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";
import { Box, Stack, List, ListItem, TextField } from "@mui/material";
import { GroupedResults, SearchSuggestions, SuggestionType, Document } from "@/types/search/suggestions-search-response";
import { Locale, localeItems } from "@/i18n/routing";
import { LocaleItem } from "@/types/contentful/locale-item";
import { NavigationType } from "@/components/header/common/header-enums";
import styles from "./styles.module.css";
import SearchSuggestionResultItem from "./search-suggestion-result-item";

interface DocGroup {
  title: string;
  docs: Document[];
}

interface MobileSearchPredictiveProps {
  locale: Locale;
  setActiveNavigation?: (activeNavigation: NavigationType) => void;
}

export default function SearchPredictive({ locale, setActiveNavigation }: MobileSearchPredictiveProps) {
  const [suggestionsData, setData] = useState<DocGroup[]>([]);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [isSearchResultsVisible, setSearchResultsIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [showMoreCharactersRequired, setShowMoreCharactersRequired] = useState(false);
  const [siteName, setSiteName] = useState("");
  const [siteLanguage, setSiteLanguage] = useState("");

  const handleSearchInputChange = (event: any) => {
    setInputValue(event.target.value);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 500); // Adjust the delay as needed (e.g., 500ms)

    return () => clearTimeout(timeoutId); // Clear timeout on cleanup
  }, [inputValue]);

  const callService = useCallback(async () => {
    if (debouncedValue) {
      // Check if input contains any numbers
      const containsNumbers = /\d/.test(inputValue);

      if (inputValue && (inputValue.length > 2 || containsNumbers)) {
        const url = "/api/search-suggestions";
        const params = { term: inputValue, datasourceId: "{BF4BC269-2552-409F-B738-8EE195735BB4}", site: siteName, language: siteLanguage };
        const options = {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8"
          },
          body: JSON.stringify(params)
        };

        const res = await fetch(url, options);
        const final = await res.json();

        const searchSuggestions: SearchSuggestions = JSON.parse(final.d);
        const grouped: GroupedResults = searchSuggestions.groupedResults;
        const suggType: SuggestionType = grouped.suggestionType;
        const { groups } = suggType;

        const docResults: DocGroup[] = [];

        groups.forEach((group) => {
          const results: Document[] = [];
          const { documents } = group;
          documents.forEach((document) => {
            results.push(document);
          });
          const docGroup = { title: group.groupValue, docs: results };
          docResults.push(docGroup);
        });

        setData(docResults);
        setSearchResultsIsVisible(true);
        setShowMoreCharactersRequired(false);
      } else if (inputValue.length < 3 && inputValue.length > 0 && !containsNumbers) {
        setShowMoreCharactersRequired(true);
        setSearchResultsIsVisible(false);
      } else if (inputValue.length === 0) {
        setShowMoreCharactersRequired(false);
        setSearchResultsIsVisible(false);
      }
    }
  }, [debouncedValue, inputValue, siteLanguage, siteName]);

  useEffect(() => {
    callService();
  }, [callService]);

  const handleClickOutside = (event: MouseEvent) => {
    if ((containerRef.current && !containerRef.current.contains(event.target as Node))) {
      setSearchResultsIsVisible(false);
      setShowMoreCharactersRequired(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, []);

  useEffect(() => {
    function IsCurrentLocale(loc: LocaleItem) {
      return loc.id === locale;
    }

    const loc: LocaleItem = localeItems.filter(IsCurrentLocale)[0];
    setSiteName(loc.siteName);
    setSiteLanguage(loc.sitecoreLanguage);
  }, [locale]);

  const getSectionBorderColor = (sectionTitle: string): string => {
    switch (sectionTitle) {
      case "SearchPage":
        return "grey.400";
      case "TourDetailPage":
        return "secondary.main";
      case "DestinationPage":
        return "#ff8102";
      case "LandingPage":
        return "#b82f41";
      case "BlogsPage":
        return "grey.400";
      case "NewsPage":
        return "secondary.main";
      default:
        return "secondary.main";
    }
  };

  const getSectionTitle = (sectionTitle: string): string => {
    switch (sectionTitle) {
      case "TourDetailPage":
        return "Tours";
      case "DestinationPage":
        return "Destination";
      case "LandingPage":
        return "Additional Info";
      case "BlogsPage":
        return "Blog Post";
      case "NewsPage":
        return "News";
      default:
        return "";
    }
  };

  // Add handler to close search when result is clicked
  const handleResultClick = () => {
    // Clear the input field
    setInputValue("");
    setSearchResultsIsVisible(false);
    setShowMoreCharactersRequired(false);

    // Close the mobile navigation menu
    if (setActiveNavigation) {
      setActiveNavigation(NavigationType.base);
    }
  };

  return (
    <Stack
      direction="column"
      spacing={0}
      sx={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        height: "100%",
        justifyContent: "center"
      }}
    >
      <Box>
        <TextField
          data-testid="predictive-search-input"
          inputRef={textFieldRef}
          variant="outlined"
          value={inputValue}
          onChange={(e) => handleSearchInputChange(e)}
          sx={{
            backgroundColor: "background.default",
            width: { xs: "350px", md: "700px" },
            borderRadius: "10px",
            "& .MuiOutlinedInput-root": {
              height: 35,
              borderRadius: "10px",
              boxShadow: 8,
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 1,
                borderColor: "grey.100"
              }
            },
            "& .MuiOutlinedInput-input": {
              fontSize: "14px",
              padding: "8px 12px"
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "grey.100"
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "grey.100"
            }
          }}
        />
      </Box>
      {/* Use separate conditional rendering to avoid nested ternaries */}
      {isSearchResultsVisible && (
        <Box
          ref={containerRef}
          sx={{
            backgroundColor: "background.default",
            position: "relative",
            zIndex: 1000,
            width: { xs: "340px", md: "650px" },
            display: "block",
            height: 0,
            overflow: "visible",
            top: "0.2rem", // Reduced space between search box and results
            border: "1px solid",
            borderColor: "grey.100",
            boxShadow: 8
          }}
        >
          <Box
            sx={{
              maxHeight: "550px",
              overflowY: "auto",
              backgroundColor: "background.default",
              "&::-webkit-scrollbar": {
                width: "8px"
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "background.warm_strong",
                borderRadius: "4px"
              },
              border: "1px solid",
              borderColor: "grey.100"
            }}
          >
            {Object.entries(suggestionsData).map(([sectionKey, sectionItems]) => (
              <List
                key={`predictive-search-results-${sectionKey}`}
                className={styles.predictiveSearchResults}
                data-testid="predictive-search-results"
                sx={{ backgroundColor: "background.default" }}>
                {sectionItems.title !== "SearchPage" ? (
                  <ListItem key={sectionKey} data-testid={`predictive-search-section-title-${sectionItems.title}`}>
                    <Box className={styles.predictiveSearchTitle} >{getSectionTitle(sectionItems.title)}</Box>
                  </ListItem>
                ) : (
                  <ListItem key={sectionKey} sx={{ display: "none" }} />
                )}
                <ListItem
                  key={`section-results-${sectionItems.title}`}
                  data-testid={`predictive-search-section-results-${sectionItems.title}`}>
                  <List key={`section-results-list-${sectionItems.title}`} sx={{ padding: 0, width: "100%" }}>
                    {sectionItems.docs.map((doc) => (
                      <ListItem key={doc.id} sx={{ padding: 0, width: "100%" }}
                        data-testid={`predictive-search-result-item-${doc.id}`}
                        onClick={handleResultClick}>
                        <SearchSuggestionResultItem
                          document={doc}
                          sectionBorderColor={getSectionBorderColor(sectionItems.title)}
                          sectionTitle={sectionItems.title}
                          searchTerm={inputValue}
                          locale={locale}
                          onResultClick={handleResultClick}
                        />
                      </ListItem>
                    ))}
                  </List>
                </ListItem>
              </List>
            ))}
          </Box>
        </Box>
      )}
      {/* Separate conditional for "more characters required" message */}
      {!isSearchResultsVisible && showMoreCharactersRequired && (
        <Box
          ref={containerRef}
          sx={{
            backgroundColor: "background.default",
            position: "relative",
            zIndex: 1000,
            width: { xs: "340px", md: "650px" },
            display: "block",
            height: 0,
            overflow: "visible",
            top: "0.2rem", // Reduced space between search box and results
            right: "4px",
            border: "1px solid",
borderColor: "grey.100",
            boxShadow: 8
          }}
        >
          <Box sx={{
            maxHeight: "350px",
            overflowY: "auto",
            backgroundColor: "background.default",
            "&::-webkit-scrollbar": {
              width: "8px"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "background.warm_strong",
              borderRadius: "4px"
            }
          }}
          >
            <List className={styles.predictiveSearchResults} sx={{ backgroundColor: "background.default" }}>
              <ListItem key="predictive-search-more-characters-required" data-testid="predictive-search-more-characters-required">
                <Box className={styles.genericTitle} >Please enter 3 or more characters</Box>
              </ListItem>
            </List>
          </Box>
        </Box>
      )}
    </Stack>
  );
};